import { useKitTemplatesData } from "@/data/use-templates-data";
import { useTypingEffect } from "@/hooks/use-typing-effect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleDown,
  faAngleUp,
  faBan,
  faCheck,
  faChevronRight,
  faCommentCheck,
  faCommentDots,
  faCopy,
  faRotateRight,
  faTrash,
  faUserGroup,
  faWrench,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import React, { useMemo, useState } from "react";
import { useAuthContext } from "../../context/auth-context";
import { useDocumentsData } from "../../data/use-documents-data";
import {
  useExpertsData,
  useRemovedKitExpertsData,
} from "../../data/use-experts-data";
import { useKitFoldersData } from "../../data/use-kit-folders-data";
import { useLibraryFilesData } from "../../data/use-library-files-data";
import {
  ExtendedMessageDto,
  useMessagesData,
} from "../../data/use-messages-data";
import { useGroupUuid } from "../../hooks/use-group-uuid";
import { useKitUuid } from "../../hooks/use-kit-uuid";
import { smartDate } from "../../lib/date";
import { renderTiptapMention, replaceMentions } from "../../lib/mentions";
import { getInitials } from "../../lib/string";
import { cn } from "../../lib/utils";
import { ExpertAvatar } from "../Expert/ExpertAvatar";
import { Dots } from "../Indicator/Dots";
import { ChatMarkdownImage } from "../markdown/ChatMarkdownImage";
import { Markdown } from "../markdown/Markdown";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import { Row } from "../ui/flex";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { useAssistContext } from "./AssistContext";

/** Sync with node/services/brain/src/routes/groups/kits/chats/multiple-experts-chat.service.ts  */
const SUMMARY_SEPARATOR = "\n\n---\n\n### 👥 Summary\n\n";

const CHAT_MARKDOWN_COMPONENTS = {
  img: ChatMarkdownImage,
};

export const checkIfStreaming = (messages: ExtendedMessageDto[]) => {
  const aiMessages = messages.filter((message) => message.type === "model");
  const isStreaming = aiMessages.some((msg) => msg.isStreaming);

  return isStreaming;
};

export const AssistAssistantMessage: React.FC<{
  messages: ExtendedMessageDto[];
  isLastMessageInMainThread: boolean;
}> = ({ messages, isLastMessageInMainThread }) => {
  // Use either single message or multiple messages array
  const firstMessage = messages.length > 0 ? messages[0] : null;

  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();
  const { activeChatUuid, capabilities, modelProfileUuid } = useAssistContext();
  const { experts: kitExperts } = useExpertsData(groupUuid, kitUuid);
  const { experts: removedExperts } = useRemovedKitExpertsData(
    groupUuid,
    kitUuid,
  );
  const {
    messages: allMessages,
    createMessageStage,
    deleteMessage,
    createMessage,
    pendingToolApproval,
  } = useMessagesData(groupUuid, kitUuid, activeChatUuid);

  const { expert, isExpertRemoved } = useExpertLookup(
    kitExperts,
    removedExperts,
    firstMessage?.chat.expertUuid ?? undefined,
  );

  const isStreaming =
    checkIfStreaming(messages) ||
    (isLastMessageInMainThread && createMessageStage !== "idle");

  const handleRetry = async () => {
    const messages = allMessages
      .filter(
        (msg) =>
          msg.type === "user" && msg.order < (firstMessage?.order ?? Infinity),
      )
      .sort((a, b) => b.order - a.order);

    const precedingHumanMessage = messages.length > 0 ? messages[0] : null;

    if (precedingHumanMessage) {
      // Delete the human message (which will cascade and delete this AI message too)
      await deleteMessage(precedingHumanMessage.uuid);

      if (!firstMessage?.chat.expertUuid) {
        throw new Error("We need an expert selected");
      }

      createMessage({
        prompt: precedingHumanMessage.content,
        capabilities,
        modelProfileUuid,
      });
    }
  };

  if (!firstMessage) return null;

  return (
    <div className="flex flex-row items-start justify-between gap-4">
      <div
        className={cn(
          "bg-background flex w-[90%] flex-col gap-2 rounded-lg border p-4",
          {
            "bg-background/50": isExpertRemoved,
          },
        )}
      >
        <Row justify="space-between" align="center">
          <Row className={"flex items-center gap-2"}>
            {isExpertRemoved && <ExpertRemovedIcon />}
            {expert && (
              <>
                <ExpertAvatar expert={expert} size="sm" />
                <span
                  className={cn("text-sm font-bold", {
                    "opacity-50": isExpertRemoved,
                  })}
                >
                  {expert.name}
                </span>
              </>
            )}
            {!isExpertRemoved && !expert && (
              <div className="flex flex-row items-center gap-2 text-sm font-bold">
                <FontAwesomeIcon icon={faUserGroup} /> Summary
              </div>
            )}
          </Row>

          <Row align="center" className="gap-2">
            {!isExpertRemoved && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRetry}
                className="h-6 w-6"
                title="Retry this response"
              >
                <FontAwesomeIcon icon={faRotateRight} className="h-3 w-3" />
              </Button>
            )}
            {firstMessage.dateCreated && (
              <span className="text-foreground-3 text-xs">
                {smartDate(firstMessage.dateCreated)}
              </span>
            )}
          </Row>
        </Row>
        <div className="space-y-2" data-testid="assist-message-ai-content">
          <Reasoning messages={messages} loading={isStreaming} />
          {messages.map((msg) => (
            <div key={msg.uuid}>
              <AiMessage
                message={msg}
                isExpertRemoved={isExpertRemoved}
                isTyping={isStreaming}
                isToolPendingApproval={
                  !!pendingToolApproval &&
                  msg.type === "tool" &&
                  (msg.content === "activate_skill" ||
                    msg.content.startsWith("Activating skill"))
                }
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const AssistToolCall: React.FC<{ type: string; loading?: boolean }> = ({
  type,
  loading,
}) => {
  const isSkippedSkill =
    type.startsWith("Skipping skill") || type.includes("user declined");
  const prettyName =
    type.startsWith("Activating skill:") || type.startsWith("Skipping skill")
      ? type
      : {
          generate_image: "Generating image...",
          activate_skill: "Activating skill",
          get_document_content: "Reading document",
          get_domain_keywords: "Analyzing domain keyword rankings",
          get_domain_traffic_metrics: "Checking domain traffic",
          get_file_content: "Reading file",
          get_keyword_data: "Researching keyword metrics",
          get_pasf_keywords: "Discovering user search behavior",
          get_perspective: "Formulating my perspective",
          get_related_keywords: "Finding related keyword opportunities",
          get_template: "Reviewing the document template",
          get_url_keywords: "Analyzing page keyword rankings",
          get_url_traffic_metrics: "Checking page traffic",
          list_documents: "Listing documents",
          list_files: "Listing files",
          list_templates: "Listing document templates",
          rag_search: "Searching knowledge",
          scrape_webpage: "Extracting web page content",
          web_search: "Searching the web",
        }[type] || type;

  return (
    <div
      className={cn(
        "font-family-standard bg-foreground-3 text-foreground flex items-center gap-2 rounded-lg border p-2 text-xs",
        { "opacity-70": isSkippedSkill },
      )}
    >
      <FontAwesomeIcon icon={faWrench} />
      <span>{prettyName}</span>
      <span className="flex h-4 items-center">
        {loading ? (
          <Dots />
        ) : (
          <FontAwesomeIcon icon={isSkippedSkill ? faBan : faCheck} />
        )}
      </span>
    </div>
  );
};

export const AssistHumanMessage: React.FC<{
  message: ExtendedMessageDto;
  showDelete?: boolean;
}> = ({ message, showDelete = false }) => {
  const { authDetails } = useAuthContext();
  const { activeChatUuid } = useAssistContext();
  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();
  const { deleteMessage } = useMessagesData(groupUuid, kitUuid, activeChatUuid);

  const initials = getInitials(authDetails?.name || "");

  const handleDelete = async () => {
    await deleteMessage(message.uuid);
  };

  return (
    <div className="flex flex-row justify-end">
      <div className="bg-background flex w-[90%] flex-col gap-2 rounded-lg border p-4">
        <Row justify="space-between" align="center">
          <span className="flex items-center gap-2">
            <Avatar className="h-6 w-6">
              <AvatarImage
                src={authDetails?.photo_url ?? undefined}
                alt={authDetails?.name || ""}
              />
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <span className={`text-sm font-bold`}>{authDetails?.name}</span>
          </span>

          <Row align="center" className="gap-2">
            {showDelete && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleDelete}
                className="h-6 w-6 text-red-500 hover:text-red-700"
                title="Delete this message and all following messages"
              >
                <FontAwesomeIcon icon={faTrash} className="h-3 w-3" />
              </Button>
            )}
            {message.dateCreated && (
              <span className="text-foreground-3 text-xs">
                {smartDate(message.dateCreated)}
              </span>
            )}
          </Row>
        </Row>
        <span className="text-foreground text-sm">
          <FormattedResponse content={message.content} />
        </span>
      </div>
    </div>
  );
};

type FormattedResponseProps = {
  content: string;
  typingEffect?: boolean;
};

const FormattedResponse: React.FC<FormattedResponseProps> = ({
  content,
  typingEffect = false,
}) => {
  const [contentWithoutSummary, summary] = content.split(SUMMARY_SEPARATOR);
  const contentTyping = useTypingEffect(contentWithoutSummary, typingEffect);
  const { format } = useFileMentionFormatter();
  const [summaryOpened, setSummaryOpened] = useState(false);

  const parts = contentTyping.split(/(>>>>>>>>>>[\s\S]*?<<<<<<<<<<)/);

  return (
    <>
      {parts.map((part, index) => {
        const isBlock =
          part.startsWith(">>>>>>>>>>") && part.endsWith("<<<<<<<<<<");

        if (isBlock) {
          const blockContent = part.slice(11, -10);

          const handleCopy = () => {
            navigator.clipboard.writeText(blockContent);
          };

          return (
            <div
              key={index}
              className={cn("mb-4 rounded-lg border border-dashed p-2")}
            >
              <Row align="center" justify="end">
                <Button
                  onClick={handleCopy}
                  variant="ghost"
                  size="icon"
                  title="Copy to clipboard"
                >
                  <FontAwesomeIcon
                    icon={faCopy}
                    className="text-foreground h-3 w-3"
                  />
                </Button>
              </Row>
              <Markdown allowRawHtml components={CHAT_MARKDOWN_COMPONENTS}>
                {format(blockContent)}
              </Markdown>
            </div>
          );
        } else {
          return part.trim() ? (
            <Markdown
              key={index}
              allowRawHtml
              components={CHAT_MARKDOWN_COMPONENTS}
            >
              {format(part)}
            </Markdown>
          ) : null;
        }
      })}
      {summary && (
        <Collapsible open={summaryOpened} onOpenChange={setSummaryOpened}>
          <CollapsibleTrigger className="border-border my-4 flex w-full cursor-pointer flex-row items-center gap-2 border-t-1 pt-4 text-sm font-bold">
            <FontAwesomeIcon
              icon={faChevronRight}
              className={cn("transition-transform", {
                "rotate-90": summaryOpened,
              })}
            />{" "}
            <FontAwesomeIcon icon={faUserGroup} /> Summary
          </CollapsibleTrigger>
          <CollapsibleContent>
            <Markdown allowRawHtml components={CHAT_MARKDOWN_COMPONENTS}>
              {format(summary)}
            </Markdown>
          </CollapsibleContent>
        </Collapsible>
      )}
    </>
  );
};

function useFileMentionFormatter() {
  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();
  const { documents } = useDocumentsData(groupUuid, kitUuid);
  const { templates } = useKitTemplatesData(groupUuid, kitUuid);
  const { files } = useKitFoldersData(groupUuid, kitUuid);
  const { libraryFiles } = useLibraryFilesData(groupUuid, kitUuid);

  return {
    format: (content: string) =>
      replaceMentions(content, (mention) => {
        return renderTiptapMention({
          mention,
          documents,
          templates,
          files,
          libraryFiles,
        });
      }),
  };
}

const useExpertLookup = <T extends { uuid: string }>(
  experts: T[],
  removedExperts: T[],
  expertUuid: string | undefined,
): { expert: T | null; isExpertRemoved: boolean } => {
  return useMemo(() => {
    if (!expertUuid) {
      return { expert: null, isExpertRemoved: false };
    }

    const allExperts = [
      ...experts.map((expert) => ({ expert, isExpertRemoved: false })),
      ...removedExperts.map((expert) => ({ expert, isExpertRemoved: true })),
    ];

    return (
      allExperts.find(({ expert }) => expert.uuid === expertUuid) ?? {
        expert: null,
        isExpertRemoved: true,
      }
    );
  }, [experts, removedExperts, expertUuid]);
};

const ExpertRemovedIcon: React.FC = () => {
  return (
    <Tooltip>
      <TooltipContent>
        <div>
          <div>This expert has been removed from your workbook.</div>
          <div>You can still see this message.</div>
        </div>
      </TooltipContent>
      <TooltipTrigger>
        <span className="text-red-500">
          <FontAwesomeIcon icon={faBan} />
        </span>
      </TooltipTrigger>
    </Tooltip>
  );
};

const AiMessage: React.FC<{
  message: ExtendedMessageDto;
  isExpertRemoved: boolean;
  isTyping: boolean;
  isToolPendingApproval?: boolean;
}> = ({ message, isExpertRemoved, isTyping, isToolPendingApproval }) => {
  if (message.type === "tool") {
    return (
      <div className={cn({ "opacity-50": isExpertRemoved })}>
        <AssistToolCall
          type={message.content}
          loading={isToolPendingApproval}
        />
      </div>
    );
  }
  if (message.type === "model") {
    return (
      <span
        className={cn("text-foreground text-sm", {
          "opacity-50": isExpertRemoved,
        })}
      >
        <FormattedResponse content={message.content} typingEffect={isTyping} />
      </span>
    );
  }
  return null;
};

const Reasoning: React.FC<{
  messages: ExtendedMessageDto[];
  loading: boolean;
  waitingForExperts?: boolean;
}> = ({ messages, loading, waitingForExperts = false }) => {
  const [expanded, setExpanded] = useState(false);
  const [recentlyExpanded, setRecentlyExpanded] = useState(false);

  const updateExpanded = (value: boolean) => {
    setExpanded(value);
    if (value) {
      setRecentlyExpanded(true);
      setTimeout(() => setRecentlyExpanded(false), 100);
    }
  };

  if (!loading && messages.length === 0) {
    return null;
  }

  const reasoningMessages = messages.filter(
    (msg) => msg.type === "reasoning" && msg.content.trim() !== "",
  );
  // Get the fist line before line break
  const lastReasoningTitle = reasoningMessages.length
    ? reasoningMessages[reasoningMessages.length - 1].content.split("\n")[0]
    : null;

  return (
    <div className="text-foreground-4 mb-4 flex flex-col items-start gap-2 text-sm italic">
      <div
        className={cn("flex flex-row items-center gap-2 font-bold", {
          "cursor-pointer": reasoningMessages.length > 0,
        })}
        onClick={
          reasoningMessages.length > 0
            ? () => updateExpanded(!expanded)
            : undefined
        }
      >
        <ThinkingIcon loading={loading || waitingForExperts} />
        {waitingForExperts ? (
          "Waiting for experts..."
        ) : loading ? (
          <>{lastReasoningTitle?.replace(/\*\*/g, "") ?? "Thinking..."}</>
        ) : reasoningMessages.length > 0 ? (
          "Show thinking"
        ) : (
          "Done"
        )}
        {reasoningMessages.length > 0 ? (
          <FontAwesomeIcon icon={expanded ? faAngleUp : faAngleDown} />
        ) : null}
      </div>
      {expanded && reasoningMessages.length > 0 && (
        <div className="flex flex-col gap-4 pt-4 pl-6">
          {reasoningMessages.map((msg) => (
            <div key={msg.uuid}>
              <TypingEffect
                text={msg.content}
                isTyping={loading && !recentlyExpanded}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TypingEffect: React.FC<{ text: string; isTyping: boolean }> = ({
  text,
  isTyping,
}) => {
  const displayedText = useTypingEffect(text, isTyping);
  return <Markdown allowRawHtml>{displayedText}</Markdown>;
};

export const ThinkingIcon: React.FC<{ loading: boolean }> = ({ loading }) => {
  return (
    <FontAwesomeIcon
      size="lg"
      icon={loading ? faCommentDots : faCommentCheck}
      className={cn({
        "animate-[bounce_500ms_linear_infinite]": loading,
      })}
    />
  );
};
